import {
  apiErrorSchema,
  listItemResponseSchema,
  listResponseSchema,
  listItemsResponseSchema,
  listsResponseSchema,
  geocodeResponseSchema,
  placeResponseSchema,
  placeReviewResponseSchema,
  placesResponseSchema,
  wishResponseSchema,
  wishesResponseSchema,
  topicResponseSchema,
  topicsResponseSchema,
  messageResponseSchema,
  messagesResponseSchema,
  eventResponseSchema,
  eventsResponseSchema,
  eventParticipantResponseSchema,
  messageReactionResponseSchema,
  eventParticipantsResponseSchema,
  inheritEventDetailsResponseSchema,
  tagResponseSchema,
  tagsResponseSchema,
  sessionResponseSchema,
  profileResponseSchema,
  reencryptResponseSchema,
  releaseMediaResponseSchema,
  sealedMediaResponseSchema,
  sealMediaResponseSchema,
  unsealedMediaResponseSchema,
  spaceResponseSchema,
  syncDeltaSchema,
  membersResponseSchema,
  inviteResponseSchema,
  invitePreviewResponseSchema,
  type ApiErrorCode,
  type ContentBlob,
  type CreateListItemRequest,
  type CreateListRequest,
  type SyncDelta,
  type UpdateListItemRequest,
  type UpdateListRequest,
  type UpdateProfileRequest,
  type CreateWishRequest,
  type CreatePlaceRequest,
  type PutPlaceReviewRequest,
  type UpdatePlaceRequest,
  type UpdateWishRequest,
  type CreateTopicRequest,
  type UpdateTopicRequest,
  type CreateMessageRequest,
  type UpdateMessageRequest,
  type CreateEventRequest,
  type UpdateEventRequest,
  type InheritEventDetailsRequest,
  type SetEventParticipationRequest,
  type SetReactionRequest,
  type NotifyRequest,
  type CreateTagRequest,
  type UpdateTagRequest,
  type TagLinkRequest,
  type ReencryptRequest,
  type WrappedKey,
} from "@vmeste/shared";
import { z, type ZodType } from "zod";

/** The notify endpoint answers with how many devices it reached. */
const notifyAckSchema = z.object({ reached: z.number().int().nonnegative() });

/**
 * The HTTP client.
 *
 * Every response is parsed through the same zod schema the server validates
 * against, so a drift between the two surfaces here as a thrown error naming the
 * field rather than as `undefined` reaching a component.
 */

export class ApiError extends Error {
  override name = "ApiError";

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    override readonly message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
  }

  /** True when retrying later could plausibly succeed. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 429;
  }

  /** True when the person needs to sign in again. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

/** Thrown when the request never reached the server. Always worth retrying. */
export class OfflineError extends Error {
  override name = "OfflineError";
  constructor() {
    super("No connection");
  }
}

export const API_BASE = "/api";

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  return send(path, schema, {
    method: options.method ?? "GET",
    headers:
      options.body === undefined ? {} : { "Content-Type": "application/json" },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

/**
 * A multipart POST, for the one thing that is bytes rather than JSON.
 *
 * No `Content-Type` header on purpose: only the browser knows the boundary it
 * generated, and setting the header by hand produces a body the server cannot
 * parse.
 */
function requestForm<T>(
  path: string,
  schema: ZodType<T>,
  form: FormData,
  method = "POST",
): Promise<T> {
  return send(path, schema, { method, body: form });
}

async function send<T>(
  path: string,
  schema: ZodType<T>,
  init: RequestInit,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      // The session is an httpOnly cookie (SPEC §8); nothing here handles a token.
      credentials: "same-origin",
      ...init,
    });
  } catch (cause) {
    // fetch only rejects when the request did not complete — DNS, offline, CORS.
    // A 500 is a resolved promise, so this branch really is "no connection".
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new OfflineError();
  }

  if (response.status === 204) {
    return schema.parse(undefined);
  }

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError(response.status, "internal", "The server sent something unreadable");
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.fields,
      );
    }
    throw new ApiError(response.status, "internal", "Something went wrong");
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      "internal",
      `The server sent an unexpected shape: ${parsed.error.issues[0]?.message ?? "unknown"}`,
    );
  }

  return parsed.data;
}

/** For 204s, where there is no body to validate. */
const nothing = {
  parse: () => undefined,
  safeParse: () => ({ success: true as const, data: undefined }),
} as unknown as ZodType<undefined>;

export const api = {
  /* ---------------------------------------------------------------- auth */

  me: (signal?: AbortSignal) =>
    request("/auth/me", sessionResponseSchema, signal ? { signal } : {}),

  signIn: (body: { email: string; password: string }) =>
    request("/auth/login", sessionResponseSchema, { method: "POST", body }),

  register: (body: {
    inviteCode: string;
    email: string;
    password: string;
    displayName: string;
    /**
     * What to call the space registration creates — read by the server only for
     * a code that names no space, and ignored outright for a code into somebody
     * else's. Omitted means the shared default name.
     */
    spaceName?: string;
  }) => request("/auth/register", sessionResponseSchema, { method: "POST", body }),

  signOut: () => request("/auth/logout", nothing, { method: "POST" }),

  /* ------------------------------------------------------------- profile */

  /** Your own name. There is no route for anyone else's. */
  updateProfile: (body: UpdateProfileRequest) =>
    request("/profile", profileResponseSchema, { method: "PATCH", body }),

  /**
   * Your own picture.
   *
   * The answer carries the new `avatarUrl` — always a path on this origin,
   * minted by the server from the bytes — so nothing here composes a media URL.
   */
  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return requestForm("/profile/avatar", profileResponseSchema, form);
  },

  removeAvatar: () =>
    request("/profile/avatar", profileResponseSchema, { method: "DELETE" }),

  /* -------------------------------------------------------------- spaces */

  spaces: () => request("/spaces", sessionResponseSchema.pick({ spaces: true })),

  createSpace: (content: ContentBlob) =>
    request("/spaces", spaceResponseSchema, { method: "POST", body: { content } }),

  renameSpace: (spaceId: string, content: ContentBlob) =>
    request(`/spaces/${spaceId}`, spaceResponseSchema, {
      method: "PATCH",
      body: { content },
    }),

  space: (spaceId: string) =>
    request(`/spaces/${spaceId}`, spaceResponseSchema),

  /**
   * Delete a space, everything written in it and every file it kept.
   *
   * Owner only, irreversible, and it takes the other member's copy with it —
   * whatever calls this is expected to have made all three of those plain
   * first.
   *
   * The server refuses the caller's *last* space with a 409: the app has no
   * screen for somebody who is in none. That is a state to explain, not a
   * request to retry, which is why the message on it is worth showing as it
   * comes.
   */
  deleteSpace: (spaceId: string) =>
    request(`/spaces/${spaceId}`, nothing, { method: "DELETE" }),

  /**
   * Turn on end-to-end encryption.
   *
   * The body is a key this server cannot open. There is no passphrase field and
   * there never will be one — see the note on the request schema.
   */
  enableEncryption: (spaceId: string, wrappedKey: WrappedKey) =>
    request(`/spaces/${spaceId}/encryption`, spaceResponseSchema, {
      method: "POST",
      body: { wrappedKey },
    }),

  /**
   * Replace the wrapped key with one under a new recovery code.
   *
   * Not a second `enableEncryption`: that route refuses a space it has already
   * locked, on purpose. This one only ever swaps the blob.
   */
  reissueRecoveryKey: (spaceId: string, wrappedKey: WrappedKey) =>
    request(`/spaces/${spaceId}/encryption/key`, spaceResponseSchema, {
      method: "PUT",
      body: { wrappedKey },
    }),

  /** One batch of the catch-up pass. See `lib/encryption`. */
  reencrypt: (spaceId: string, rows: ReencryptRequest["rows"]) =>
    request(`/spaces/${spaceId}/reencrypt`, reencryptResponseSchema, {
      method: "POST",
      body: { rows },
    }),

  /**
   * The files this space collected before it was locked, ids only.
   *
   * Capped by the server, with `more` saying another batch is waiting — the
   * same shape `reencrypt` has, and for the same reason: the pass belongs to
   * whichever device holds the key, and it is expected to be interrupted.
   */
  unsealedMedia: (spaceId: string) =>
    request(`/media/spaces/${spaceId}/unsealed`, unsealedMediaResponseSchema),

  /**
   * Swap one stored file for the sealed version of itself.
   *
   * The id does not change and no revision is claimed: the bytes are different,
   * what any row *means* is not. See `lib/media` for the pass that drives this.
   */
  sealMedia: (mediaId: string, sealed: File) => {
    const form = new FormData();
    form.append("file", sealed, sealed.name);
    return requestForm(`/media/${mediaId}/bytes`, sealMediaResponseSchema, form, "PUT");
  },

  /**
   * Store bytes this device has already sealed, and get back the path.
   *
   * Only a link preview's picture uses it: in an encrypted space the server
   * fetches that picture (CORS) but must not keep it, so it comes back in the
   * unfurl response and returns sealed.
   */
  storeSealedMedia: (spaceId: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return requestForm(`/media/spaces/${spaceId}/sealed`, sealedMediaResponseSchema, form);
  },

  /**
   * Ask the space to stop keeping a file.
   *
   * For a wish's preview picture, which is named from inside the wish's own
   * content blob — the server cannot read that, so deleting the wish cannot
   * take the picture with it. The caller must have established that no other
   * wish is showing the same file first: the server can only see the
   * references it has rows for, and it answers `kept` when one of those holds.
   */
  releaseMedia: (spaceId: string, mediaId: string) =>
    request(
      `/media/spaces/${spaceId}/${encodeURIComponent(mediaId)}`,
      releaseMediaResponseSchema,
      { method: "DELETE" },
    ),

  members: (spaceId: string) =>
    request(`/spaces/${spaceId}/members`, membersResponseSchema),

  removeMember: (spaceId: string, userId: string) =>
    request(`/spaces/${spaceId}/members/${userId}`, nothing, { method: "DELETE" }),

  /* ------------------------------------------------------------- invites */

  /**
   * Mint an invite code. Both kinds go through the one route (SPEC §4.1), told
   * apart by `kind` in the body.
   *
   * The first argument keeps the shape it had while there was only one kind — a
   * space id means "a code into that space" — and gains `{ kind: "app" }`, a
   * code that opens an account and a space of the newcomer's own and reaches
   * nothing of ours.
   *
   * The app body carries no `spaceId` at all rather than a null one: the server
   * refuses a space id on an app invite instead of quietly dropping it, so a
   * call that mislabels itself fails loudly instead of handing out a door into
   * this space under the other name. `role` is left out of it for the same
   * reason — the server always mints an owner there, of the space it makes for
   * that person, and a role sent from here would read as if this call had a say.
   */
  createInvite: (
    target: string | { kind: "space"; spaceId: string } | { kind: "app" },
    expiresInHours = 24 * 7,
  ) => {
    const into =
      typeof target === "string" ? { kind: "space" as const, spaceId: target } : target;

    return request("/invites", inviteResponseSchema, {
      method: "POST",
      body:
        into.kind === "space"
          ? { kind: "space", spaceId: into.spaceId, role: "member", expiresInHours }
          : { kind: "app", expiresInHours },
    });
  },

  previewInvite: (code: string) =>
    request(`/invites/${encodeURIComponent(code)}`, invitePreviewResponseSchema),

  acceptInvite: (inviteCode: string) =>
    request("/invites/accept", spaceResponseSchema, {
      method: "POST",
      body: { inviteCode },
    }),

  /* --------------------------------------------------------------- lists */

  lists: (spaceId: string) =>
    request(`/spaces/${spaceId}/lists`, listsResponseSchema),

  createList: (spaceId: string, body: CreateListRequest) =>
    request(`/spaces/${spaceId}/lists`, listResponseSchema, { method: "POST", body }),

  updateList: (spaceId: string, listId: string, body: UpdateListRequest) =>
    request(`/spaces/${spaceId}/lists/${listId}`, listResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteList: (spaceId: string, listId: string) =>
    request(`/spaces/${spaceId}/lists/${listId}`, nothing, { method: "DELETE" }),

  /* --------------------------------------------------------------- items */

  items: (spaceId: string) =>
    request(`/spaces/${spaceId}/items`, listItemsResponseSchema),

  createItem: (spaceId: string, body: CreateListItemRequest) =>
    request(`/spaces/${spaceId}/items`, listItemResponseSchema, {
      method: "POST",
      body,
    }),

  updateItem: (spaceId: string, itemId: string, body: UpdateListItemRequest) =>
    request(`/spaces/${spaceId}/items/${itemId}`, listItemResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteItem: (spaceId: string, itemId: string) =>
    request(`/spaces/${spaceId}/items/${itemId}`, nothing, { method: "DELETE" }),

  /* -------------------------------------------------------------- wishes */

  wishes: (spaceId: string) =>
    request(`/spaces/${spaceId}/wishes`, wishesResponseSchema),

  createWish: (spaceId: string, body: CreateWishRequest) =>
    request(`/spaces/${spaceId}/wishes`, wishResponseSchema, { method: "POST", body }),

  updateWish: (spaceId: string, wishId: string, body: UpdateWishRequest) =>
    request(`/spaces/${spaceId}/wishes/${wishId}`, wishResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteWish: (spaceId: string, wishId: string) =>
    request(`/spaces/${spaceId}/wishes/${wishId}`, nothing, { method: "DELETE" }),

  /* -------------------------------------------------------------- places */

  /**
   * A place by name, looked up through our own server (SPEC §4.10).
   *
   * `near` is the box the map is showing, so the nearest match comes first.
   * Nothing about where the person actually is goes into it.
   */
  geocode: (query: string, near?: [number, number, number, number]) => {
    const params = new URLSearchParams({ q: query });
    if (near) params.set("near", near.join(","));
    return request(`/geocode?${params.toString()}`, geocodeResponseSchema);
  },


  places: (spaceId: string) =>
    request(`/spaces/${spaceId}/places`, placesResponseSchema),

  createPlace: (spaceId: string, body: CreatePlaceRequest) =>
    request(`/spaces/${spaceId}/places`, placeResponseSchema, { method: "POST", body }),

  updatePlace: (spaceId: string, placeId: string, body: UpdatePlaceRequest) =>
    request(`/spaces/${spaceId}/places/${placeId}`, placeResponseSchema, {
      method: "PATCH",
      body,
    }),

  deletePlace: (spaceId: string, placeId: string) =>
    request(`/spaces/${spaceId}/places/${placeId}`, nothing, { method: "DELETE" }),

  reviewPlace: (spaceId: string, placeId: string, body: PutPlaceReviewRequest) =>
    request(`/spaces/${spaceId}/places/${placeId}/review`, placeReviewResponseSchema, {
      method: "PUT",
      body,
    }),

  unreviewPlace: (spaceId: string, placeId: string) =>
    request(`/spaces/${spaceId}/places/${placeId}/review`, nothing, { method: "DELETE" }),

  /* ---------------------------------------------------------------- chat */

  topics: (spaceId: string) =>
    request(`/spaces/${spaceId}/topics`, topicsResponseSchema),

  createTopic: (spaceId: string, body: CreateTopicRequest) =>
    request(`/spaces/${spaceId}/topics`, topicResponseSchema, { method: "POST", body }),

  updateTopic: (spaceId: string, topicId: string, body: UpdateTopicRequest) =>
    request(`/spaces/${spaceId}/topics/${topicId}`, topicResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteTopic: (spaceId: string, topicId: string) =>
    request(`/spaces/${spaceId}/topics/${topicId}`, nothing, { method: "DELETE" }),

  messages: (spaceId: string, topicId?: string) =>
    request(
      `/spaces/${spaceId}/messages${topicId ? `?topicId=${topicId}` : ""}`,
      messagesResponseSchema,
    ),

  createMessage: (spaceId: string, body: CreateMessageRequest) =>
    request(`/spaces/${spaceId}/messages`, messageResponseSchema, {
      method: "POST",
      body,
    }),

  updateMessage: (spaceId: string, messageId: string, body: UpdateMessageRequest) =>
    request(`/spaces/${spaceId}/messages/${messageId}`, messageResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteMessage: (spaceId: string, messageId: string) =>
    request(`/spaces/${spaceId}/messages/${messageId}`, nothing, { method: "DELETE" }),

  /** How far this person has read. Deliberately not a space-wide change. */
  markRead: (spaceId: string, messageId: string) =>
    request(`/spaces/${spaceId}/messages/read`, nothing, {
      method: "POST",
      body: { messageId },
    }),

  /* ------------------------------------------------------------ calendar */

  events: (spaceId: string, window: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams();
    if (window.from) query.set("from", window.from);
    if (window.to) query.set("to", window.to);
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return request(`/spaces/${spaceId}/events${suffix}`, eventsResponseSchema);
  },

  createEvent: (spaceId: string, body: CreateEventRequest) =>
    request(`/spaces/${spaceId}/events`, eventResponseSchema, { method: "POST", body }),

  updateEvent: (spaceId: string, eventId: string, body: UpdateEventRequest) =>
    request(`/spaces/${spaceId}/events/${eventId}`, eventResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteEvent: (spaceId: string, eventId: string) =>
    request(`/spaces/${spaceId}/events/${eventId}`, nothing, { method: "DELETE" }),

  /**
   * Carry an event's files and answers onto another one.
   *
   * The one caller is the split that editing a single day of a series makes:
   * the copy that takes the day's place inherits the fields of the form, and
   * this is what stops the tickets and everybody's «иду» being left behind on
   * the series. It cannot be done from here — the bytes of an attachment are
   * not on this device, so a client could only carry a file over by uploading a
   * second copy of it, and only if it happened to have one.
   *
   * Safe to call twice: the server derives each copied row's id from the pair
   * it copies, so a repeat writes nothing and answers with two zeroes.
   */
  inheritEventDetails: (spaceId: string, eventId: string, fromEventId: string) =>
    request(
      `/spaces/${spaceId}/events/${eventId}/inherit`,
      inheritEventDetailsResponseSchema,
      {
        method: "POST",
        body: { fromEventId } satisfies InheritEventDetailsRequest,
      },
    ),

  eventParticipants: (spaceId: string, eventId: string) =>
    request(
      `/spaces/${spaceId}/events/${eventId}/participants`,
      eventParticipantsResponseSchema,
    ),

  /**
   * Say whether *you* are coming.
   *
   * `me` is a literal in the path and there is no user in the body, so there is
   * no argument to this function that could name anybody else. That is the point
   * of the shape, not an oversight: participation is yours alone to state (SPEC
   * §4.4).
   */
  setParticipation: (
    spaceId: string,
    eventId: string,
    body: SetEventParticipationRequest,
  ) =>
    request(
      `/spaces/${spaceId}/events/${eventId}/participants/me`,
      eventParticipantResponseSchema,
      { method: "PUT", body },
    ),

  /**
   * React to a message, or take it back — `emoji: null`.
   *
   * `me` in the path and no person in the body, for the same reason
   * `setParticipation` has none: a reaction is yours alone to give (SPEC §4.5).
   */
  react: (spaceId: string, messageId: string, body: SetReactionRequest) =>
    request(
      `/spaces/${spaceId}/messages/${messageId}/reactions/me`,
      messageReactionResponseSchema,
      { method: "PUT", body },
    ),

  /* ---------------------------------------------------------------- tags */

  tags: (spaceId: string) => request(`/spaces/${spaceId}/tags`, tagsResponseSchema),

  createTag: (spaceId: string, body: CreateTagRequest) =>
    request(`/spaces/${spaceId}/tags`, tagResponseSchema, { method: "POST", body }),

  updateTag: (spaceId: string, tagId: string, body: UpdateTagRequest) =>
    request(`/spaces/${spaceId}/tags/${tagId}`, tagResponseSchema, {
      method: "PATCH",
      body,
    }),

  deleteTag: (spaceId: string, tagId: string) =>
    request(`/spaces/${spaceId}/tags/${tagId}`, nothing, { method: "DELETE" }),

  linkTag: (spaceId: string, body: TagLinkRequest) =>
    request(`/spaces/${spaceId}/tags/link`, nothing, { method: "POST", body }),

  unlinkTag: (spaceId: string, body: TagLinkRequest) =>
    request(`/spaces/${spaceId}/tags/unlink`, nothing, { method: "POST", body }),

  /* -------------------------------------------------------------- notify */

  /**
   * Ask for the other person to be told (SPEC §3, decision 3).
   *
   * The summary is written and encrypted here, on the device that acted; the
   * server forwards the blob without reading it. Best-effort by nature — a
   * failure must never undo the change it describes.
   */
  notify: (spaceId: string, body: NotifyRequest) =>
    request(`/spaces/${spaceId}/notify`, notifyAckSchema, { method: "POST", body }),

  /* ---------------------------------------------------------------- sync */

  sync: (spaceId: string, since: number, signal?: AbortSignal): Promise<SyncDelta> =>
    request(
      `/spaces/${spaceId}/sync?since=${since}`,
      syncDeltaSchema,
      signal ? { signal } : {},
    ),
};

export type Api = typeof api;
